import { ComponentType, ReactElement } from 'react';

import { ConditionComparator, FieldTypeKind, FormFieldValue } from '@components/form/v2';
import { HorizontalTabItem } from '@components/horizontal-tabs';
import { IconProps } from '@components/icons';

import { StateField } from './state';

type FieldGroup = 'content' | 'structure' | 'inputs' | 'selections' | 'special';

export type FieldComponent<T, Y extends object = {}> = ComponentType<
  {
    field: Extract<StateField, { kind: T }>;
  } & Y
>;

export type ConditionValueMenu<K extends FieldTypeKind> = FieldComponent<
  K,
  {
    comparator: ConditionComparator;
    value?: FormFieldValue;
    onSelect(value: FormFieldValue): void;
  }
>;

export interface FieldConfig<K extends FieldTypeKind = FieldTypeKind> {
  kind: K;
  Icon: ComponentType<IconProps>;
  title: string;
  Component: ComponentType<{ field: Extract<StateField, { kind: K }>; dragHandle?: ReactElement }>;
  Display: ComponentType<{ field: Extract<StateField, { kind: K }> }>;
  TreeDisplay: FieldComponent<K>;
  group?: FieldGroup;
  sidebarTitle?: string;
  propertiesTabs?: Array<(field: Extract<StateField, { kind: K }>) => HorizontalTabItem>;
  condition?: {
    comparators: Array<ConditionComparator>;
    ValueMenu: ConditionValueMenu<K>;
  };
}

const registry = new Map<FieldTypeKind, FieldConfig<any>>();

export const register = <T extends FieldTypeKind>(field: FieldConfig<T>) => {
  registry.set(field.kind, field);
};

export const clearRegistry = () => {
  registry.clear();
};

export const kindExistsInRegistry = (kind: FieldTypeKind) => registry.has(kind);

export const getFieldConfigForKind = <T extends FieldTypeKind = any>(kind: T) => {
  const field = registry.get(kind);
  if (!field) throw new Error(`Kind ${kind} has no registered field`);
  return field as Extract<FieldConfig<T>, { kind: T }>;
};

export const getFieldGroups = () => {
  return Array.from(registry.values()).reduce(
    (result, field) => {
      if (field.group) {
        result[field.group].push(field);
      }
      return result;
    },
    {
      content: [],
      structure: [],
      inputs: [],
      selections: [],
      special: [],
    } as {
      content: Array<FieldConfig<any>>;
      structure: Array<FieldConfig<any>>;
      inputs: Array<FieldConfig<any>>;
      selections: Array<FieldConfig<any>>;
      special: Array<FieldConfig<any>>;
    }
  );
};
